import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminTables() {
  const [tables, setTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    table_number: '', min_persons: 1, max_persons: 4,
    area_id: '', active: true, combinable: false, priority: 100,
    split_enabled: false, split_online: false, split_auto_assign: false,
    split_max_parts: 2, split_min_persons: 1, split_rule: 'free',
    online_bookable: true,
  });

  const load = async () => {
    const [t, a] = await Promise.all([api.get('/tables'), api.get('/areas')]);
    setTables(t.data);
    setAreas(a.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing(null);
    setForm({ table_number: '', min_persons: 1, max_persons: 4, area_id: areas[0]?.id || '', active: true, combinable: false, priority: 100,
      split_enabled: false, split_online: false, split_auto_assign: false, split_max_parts: 2, split_min_persons: 1, split_rule: 'free', online_bookable: true });
    setShowForm(true);
  };
  const startEdit = (t) => {
    setEditing(t);
    setForm({
      table_number: t.table_number,
      min_persons: t.min_persons, max_persons: t.max_persons,
      area_id: t.area_id, active: t.active,
      combinable: t.combinable, priority: t.priority,
      split_enabled: !!t.split_enabled, split_online: !!t.split_online,
      split_auto_assign: !!t.split_auto_assign,
      split_max_parts: t.split_max_parts ?? 2, split_min_persons: t.split_min_persons ?? 1,
      split_rule: t.split_rule || 'free',
      online_bookable: t.online_bookable !== false,
    });
    setShowForm(true);
  };
  const save = async () => {
    if (!form.table_number || !form.area_id) {
      toast.error('Tischnummer und Bereich erforderlich');
      return;
    }
    try {
      if (editing) {
        await api.put(`/tables/${editing.id}`, form);
        toast.success('Aktualisiert');
      } else {
        await api.post('/tables', form);
        toast.success('Erstellt');
      }
      setShowForm(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };
  const del = async (t) => {
    if (!window.confirm(`Tisch ${t.table_number} löschen?`)) return;
    try {
      await api.delete(`/tables/${t.id}`);
      toast.success('Gelöscht');
      load();
    } catch (e) { toast.error('Fehler'); }
  };
  const toggleActive = async (t) => {
    await api.put(`/tables/${t.id}`, { active: !t.active });
    load();
  };

  // group by area
  const grouped = areas.map(a => ({ area: a, tables: tables.filter(t => t.area_id === a.id) }));
  const orphans = tables.filter(t => !areas.find(a => a.id === t.area_id));

  return (
    <div className="p-4 md:p-8 max-w-5xl" data-testid="admin-tables-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]">Verwaltung</p>
          <h1 className="font-display text-3xl text-[#1C1C19]">Tische</h1>
        </div>
        <button onClick={startNew} data-testid="table-add-btn" disabled={areas.length === 0} className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm flex items-center gap-2 transition disabled:bg-[#9CA3AF]">
          <Plus className="w-4 h-4" /> Tisch anlegen
        </button>
      </header>

      {areas.length === 0 && (
        <div className="bg-[#FFFBEB] border border-[#EAB308] rounded p-4 mb-4 text-sm" data-testid="tables-no-area-warning">
          Bitte zuerst einen Bereich anlegen.
        </div>
      )}

      <div className="space-y-6">
        {grouped.map(({ area, tables: areaTables }) => (
          <div key={area.id} className="bg-white border border-[#E5E5E0] rounded-lg" data-testid={`tables-area-${area.id}`}>
            <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center gap-2">
              <span className="w-3 h-3 rounded" style={{ background: area.color }}></span>
              <h2 className="font-display text-lg">{area.name}</h2>
              <span className="text-xs text-[#5C5C59]">({areaTables.length})</span>
            </div>
            {areaTables.length === 0 ? (
              <div className="p-4 text-sm text-[#9CA3AF]">Keine Tische in diesem Bereich.</div>
            ) : (
              <div className="divide-y divide-[#E5E5E0]">
                {areaTables.map(t => (
                  <div key={t.id} className="p-3 flex items-center gap-3" data-testid={`table-row-${t.id}`}>
                    <div className="w-10 h-10 rounded border-2 flex items-center justify-center font-display text-lg flex-shrink-0" style={{ borderColor: area.color, color: '#1C1C19' }}>
                      {t.table_number}
                    </div>
                    <div className="flex-1 text-sm">
                      <div className="font-medium">Tisch {t.table_number}</div>
                      <div className="text-xs text-[#5C5C59]">{t.min_persons}–{t.max_persons} Personen · Prio {t.priority}{t.combinable ? ' · kombinierbar' : ''}</div>
                    </div>
                    {t.split_enabled && <span className="text-xs px-2 py-0.5 bg-[#9333EA]/15 text-[#9333EA] rounded font-medium" data-testid={`table-split-badge-${t.id}`}>Splitting</span>}
                    {t.online_bookable === false && <span className="text-xs px-2 py-0.5 bg-[#B45309]/15 text-[#B45309] rounded font-medium" data-testid={`table-inhouse-badge-${t.id}`}>Nur im Haus</span>}
                    {!t.active && <span className="text-xs px-2 py-0.5 bg-[#EF4444]/15 text-[#EF4444] rounded">Inaktiv</span>}
                    <button onClick={() => toggleActive(t)} data-testid={`table-toggle-${t.id}`} className="text-xs px-3 py-1.5 border border-[#E5E5E0] rounded hover:border-[#1C1C19]">
                      {t.active ? 'Sperren' : 'Aktivieren'}
                    </button>
                    <button onClick={() => startEdit(t)} data-testid={`table-edit-${t.id}`} className="p-2 hover:bg-[#F9F9F7] rounded"><Edit className="w-4 h-4 text-[#5C5C59]" /></button>
                    <button onClick={() => del(t)} data-testid={`table-delete-${t.id}`} className="p-2 hover:bg-[#FEE2E2] rounded"><Trash2 className="w-4 h-4 text-[#EF4444]" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="table-form">
            <h2 className="font-display text-xl mb-4">{editing ? 'Tisch bearbeiten' : 'Neuer Tisch'}</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium block mb-1">Tischnummer / -name</label>
                <input value={form.table_number} onChange={e => setForm({...form, table_number: e.target.value})} data-testid="table-form-number" className="w-full px-3 py-2 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19]" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Bereich</label>
                <select value={form.area_id} onChange={e => setForm({...form, area_id: e.target.value})} data-testid="table-form-area" className="w-full px-3 py-2 border border-[#E5E5E0] rounded bg-white">
                  {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium block mb-1">Min. Personen</label>
                  <input type="number" min="1" value={form.min_persons} onChange={e => setForm({...form, min_persons: parseInt(e.target.value)})} data-testid="table-form-min" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Max. Personen</label>
                  <input type="number" min="1" value={form.max_persons} onChange={e => setForm({...form, max_persons: parseInt(e.target.value)})} data-testid="table-form-max" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Priorität (höher = bevorzugt)</label>
                <input type="number" value={form.priority} onChange={e => setForm({...form, priority: parseInt(e.target.value) || 0})} data-testid="table-form-priority" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} data-testid="table-form-active" />
                Aktiv
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.combinable} onChange={e => setForm({...form, combinable: e.target.checked})} data-testid="table-form-combinable" />
                Kombinierbar
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!form.online_bookable} onChange={e => setForm({...form, online_bookable: !e.target.checked})} data-testid="table-form-in-house-only" />
                Nur im Haus (nicht online buchbar)
              </label>
              <p className="text-xs text-[#9CA3AF] -mt-2">Wenn aktiv, wird dieser Tisch Gästen online nicht angeboten – er bleibt für Walk-in & manuelle Reservierungen im Admin nutzbar.</p>

              {/* ===== Smart-Tischsplitting ===== */}
              <div className="pt-3 mt-2 border-t border-[#E5E5E0]">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={form.split_enabled} onChange={e => setForm({...form, split_enabled: e.target.checked})} data-testid="table-form-split-enabled" />
                  Smart-Tischsplitting aktivieren
                </label>
                <p className="text-xs text-[#9CA3AF] mt-1">Freie Restplätze dieses Tisches können bis zur nächsten Reservierung genutzt werden. Die Kapazität wird nie überschritten.</p>
                {form.split_enabled && (
                  <div className="mt-3 space-y-3 pl-1" data-testid="table-form-split-options">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.split_online} onChange={e => setForm({...form, split_online: e.target.checked})} data-testid="table-form-split-online" />
                      Restplätze auch online anbieten <span className="text-xs text-[#9CA3AF]">(sonst nur Admin/Telefon)</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.split_auto_assign} onChange={e => setForm({...form, split_auto_assign: e.target.checked})} data-testid="table-form-split-auto" />
                      Freie Restplätze automatisch vergeben (Auto-Zuweisung)
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-medium block mb-1">Max. Teilreservierungen</label>
                        <input type="number" min="2" max="6" value={form.split_max_parts} onChange={e => setForm({...form, split_max_parts: parseInt(e.target.value) || 2})} data-testid="table-form-split-parts" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                      </div>
                      <div>
                        <label className="text-sm font-medium block mb-1">Min. Pers. je Teil</label>
                        <input type="number" min="1" value={form.split_min_persons} onChange={e => setForm({...form, split_min_persons: parseInt(e.target.value) || 1})} data-testid="table-form-split-min" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium block mb-1">Teilungsregel</label>
                      <select value={form.split_rule} onChange={e => setForm({...form, split_rule: e.target.value})} data-testid="table-form-split-rule" className="w-full px-3 py-2 border border-[#E5E5E0] rounded bg-white">
                        <option value="free">Frei (beliebige Aufteilung)</option>
                        <option value="2+2">2 + 2</option>
                        <option value="2+3">2 + 3</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowForm(false)} data-testid="table-form-cancel" className="px-4 py-2 border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19]">Abbrechen</button>
              <button onClick={save} data-testid="table-form-save" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm">Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
